import { z } from 'zod'
import { todayManila, addDays } from '@/lib/format'

// ── Property type enum (mirrors DB enum) ──────────────────────────────────────

export const PROPERTY_TYPES = [
  'hotel',
  'apartment',
  'resort',
  'villa',
  'bed_and_breakfast',
  'hostel',
  'guesthouse',
  'condo',
] as const

export type PropertyType = (typeof PROPERTY_TYPES)[number]

export const PROPERTY_TYPE_LABELS: Record<PropertyType, string> = {
  hotel:            'Hotel',
  apartment:        'Apartment',
  resort:           'Resort',
  villa:            'Villa',
  bed_and_breakfast:'B&B',
  hostel:           'Hostel',
  guesthouse:       'Guesthouse',
  condo:            'Condo',
}

// ── Search params schema ──────────────────────────────────────────────────────
// All fields are optional so the search page can work without a full query.
// Defaults are applied at read-time (not stored in the URL).

export const searchParamsSchema = z.object({
  city:       z.string().optional(),
  province:   z.string().optional(),   // disambiguates same-named places (e.g. Mactan in Cebu vs Masbate)
  check_in:   z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  check_out:  z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  adults:     z.coerce.number().int().min(1).max(30).optional(),
  children:   z.coerce.number().int().min(0).max(20).optional(),
  child_ages: z.string().optional(),   // comma-separated child ages 0–11, e.g. "5,6"
  rooms:      z.coerce.number().int().min(1).max(10).optional(),
  types:      z.string().optional(),   // comma-separated PropertyType values
  min_price:  z.coerce.number().int().min(0).optional(),
  max_price:  z.coerce.number().int().min(0).optional(),
  amenities:  z.string().optional(),   // comma-separated amenity keys
  page:       z.coerce.number().int().min(1).optional(),
})

export type SearchParams = z.infer<typeof searchParamsSchema>

// ── Resolved params with defaults applied ─────────────────────────────────────

export interface ResolvedSearchParams {
  city:       string
  province:   string
  check_in:   string
  check_out:  string
  adults:     number
  children:   number
  child_ages: number[]
  rooms:      number
  types:      PropertyType[]
  min_price:  number | undefined
  max_price:  number | undefined
  amenities:  string[]
  page:       number
  limit:      number
  offset:     number
}

const PAGE_SIZE = 20

export function resolveSearchParams(raw: SearchParams): ResolvedSearchParams {
  const today    = todayManila()
  const tomorrow = addDays(today, 1)
  const page     = raw.page ?? 1

  // child_ages may arrive as a comma-string (from the URL) or already as a
  // number[] (when callers pass pre-resolved values back through, e.g. Home).
  const rawAges: unknown = (raw as { child_ages?: unknown }).child_ages
  const childAges: number[] = (
    Array.isArray(rawAges)
      ? rawAges
      : typeof rawAges === 'string' && rawAges
        ? rawAges.split(',')
        : []
  )
    .map(Number)
    .filter((n) => Number.isInteger(n) && n >= 0 && n <= 11)

  return {
    city:      raw.city      ?? '',
    province:  raw.province  ?? '',
    check_in:  raw.check_in  ?? today,
    check_out: raw.check_out ?? tomorrow,
    adults:    raw.adults    ?? 1,
    // child_ages is the source of truth for the count when present
    children:  childAges.length > 0 ? childAges.length : (raw.children ?? 0),
    child_ages: childAges,
    rooms:     raw.rooms     ?? 1,
    types:     raw.types
      ? (raw.types.split(',').filter(Boolean) as PropertyType[])
      : [],
    min_price: raw.min_price,
    max_price: raw.max_price,
    amenities: raw.amenities
      ? raw.amenities.split(',').filter(Boolean)
      : [],
    page,
    limit:  PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }
}

// ── Serialise back to URLSearchParams ─────────────────────────────────────────

export function toURLSearchParams(p: Partial<ResolvedSearchParams>): URLSearchParams {
  const out = new URLSearchParams()
  if (p.city)       out.set('city',      p.city)
  if (p.province)   out.set('province',  p.province)
  if (p.check_in)   out.set('check_in',  p.check_in)
  if (p.check_out)  out.set('check_out', p.check_out)
  if (p.adults   != null) out.set('adults',    String(p.adults))
  if (p.children != null) out.set('children',  String(p.children))
  if (p.child_ages?.length) out.set('child_ages', p.child_ages.join(','))
  if (p.rooms    != null) out.set('rooms',     String(p.rooms))
  if (p.types?.length)    out.set('types',     p.types.join(','))
  if (p.min_price != null) out.set('min_price', String(p.min_price))
  if (p.max_price != null) out.set('max_price', String(p.max_price))
  if (p.amenities?.length) out.set('amenities', p.amenities.join(','))
  if (p.page && p.page > 1) out.set('page', String(p.page))
  return out
}
